import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { DateTime } from "luxon";
import { ReactElement, ReactNode } from "react";

import {
  CampaignStatus,
  InboxType,
  InitialMessageSendFailureType,
} from "~frontend/__generated__/graphql";
import ClickToCopy from "~frontend/components/ClickToCopy";
import { FlexBox } from "~frontend/components/FlexBox";
import InfoToolip from "~frontend/components/InfoTooltip";
import { InternalLink, LinkButton } from "~frontend/components/Link";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import { MetadataValuesDetails } from "~frontend/components/campaignMetadata/MetadataValuesDetails";
import {
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import {
  formatShortTime,
  timezoneDisplayName,
} from "~frontend/util/dateFormat";
import determineFilename from "~frontend/util/determineImportFilename";
import { displayPhoneNumber } from "~frontend/util/phoneNumber";
import { plural } from "~frontend/util/plural";

import FollowUpCampaignFilterSummary from "../campaignEditor/campaignEditorUtils/FollowUpCampaignFilterSummary";
import { CampaignForEditor } from "../campaignEditor/types";
import { blastStatusToDisplayText } from "./BlastSendStatus";
import { buildApproverLink } from "./approverLink";
import InitialSendComponents from "./initialSend";
import { CampaignStatsForDetailQueryResult } from "./stats/useCampaignStats";
import { buildTexterLink } from "./texterLink";
import { CampaignForView } from "./types";

const statusToDisplayText = (
  status: CampaignStatus,
  texterSendEnabled?: boolean,
  approvalsRequired?: boolean
) => {
  switch (status) {
    case CampaignStatus.Archived:
      return "is Archived";
    case CampaignStatus.Draft:
      return "is a Draft";
    case CampaignStatus.InitialSendReady:
      if (texterSendEnabled) {
        return "has Texter Send On";
      }
      if (approvalsRequired) {
        return "is Ready to Approve";
      }
      return "is Ready to Send";
    case CampaignStatus.InitialSendScheduled:
      return "is Scheduled to Send";
    case CampaignStatus.InitialSendRunning:
    case CampaignStatus.RoiTestAutosendPending:
    case CampaignStatus.PartialSendEvaluationPending:
      if (texterSendEnabled) {
        return "has Texter Send On";
      }
      return "is Running";
    case CampaignStatus.InitialSendCanceling:
      return "is Cancelling";
    case CampaignStatus.InitialSendComplete:
      return "Finished Sending";
    default: {
      const _exhaustiveCheck: never = status;
      return _exhaustiveCheck;
    }
  }
};

const TexterInformation = ({
  campaign,
  stats,
}: {
  campaign: CampaignForView;
  stats: CampaignStatsForDetailQueryResult;
}): ReactElement | null => {
  const { subaccountLink } = useSubaccount()!;
  const texterLink = buildTexterLink(campaign);
  return (
    <Card sx={{ marginBottom: 1, background: "none", mt: 1 }}>
      <CardHeader
        title={<Typography variant="body2Bold">Texter Information</Typography>}
        sx={{ height: "28px" }}
      />
      <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
        <TwoColumnDisplay
          labelVariant="body2Bold"
          allRowSx={{ fontSize: "12px" }}
          labelWidth={143}
          showTopBorder={false}
          showBottomBorder={false}
          rows={[
            {
              key: "replies",
              label: "Replies",
              content: (
                <FlexBox justifyContent="space-between">
                  <Box>
                    {stats.state === "loading" && "Loading..."}
                    {stats.state === "error" && "Error loading stats"}
                    {stats.state === "loaded" && (
                      <>
                        {plural(
                          stats.data.threadsNeedingResponse,
                          "contact",
                          "contacts"
                        )}{" "}
                        need a reply
                      </>
                    )}
                  </Box>
                  <Box>
                    <InternalLink
                      component={InternalLink}
                      to={subaccountLink(
                        `/threads?context=${campaign.id}&campaign_ids=${campaign.id}`
                      )}
                    >
                      View Threads
                    </InternalLink>
                  </Box>
                </FlexBox>
              ),
            },
            {
              key: "texter-link",
              label: <FlexBox alignItems="center">Texter Link</FlexBox>,
              visible: campaign.inbox.type === InboxType.Campaign,
              content: (
                <ClickToCopy
                  text={texterLink.fullLink}
                  IconButtonProps={{
                    sx: { color: palette.blue, padding: 0, width: "16px" },
                  }}
                  iconSx={{ width: "16px" }}
                  TextProps={{
                    sx: { marginTop: "4px", marginLeft: "8px" },
                  }}
                >
                  <InternalLink
                    to={texterLink.path}
                    target="_blank"
                    sx={{
                      color: palette.blue,
                      textDecoration: "none",
                    }}
                    data-testid="texter-link"
                  >
                    {texterLink.fullLink.substring(0, 40)}...
                  </InternalLink>
                </ClickToCopy>
              ),
            },
            {
              key: "inbox-name",
              label: <FlexBox alignItems="center">Inbox</FlexBox>,
              visible: Boolean(
                campaign.inbox.type === InboxType.General && campaign.inbox.name
              ),
              content: <label>{campaign.inbox.name}</label>,
            },
          ]}
        />
      </CardContent>
    </Card>
  );
};

export const ApprovalsInformation = ({
  campaign,
}: {
  campaign: CampaignForView;
}): ReactElement | null => {
  const { subaccountLink } = useSubaccount()!;

  if (campaign.clickerApprovals.required === 0) {
    return null;
  }

  const approverLink = buildApproverLink(campaign, subaccountLink);

  const approvalsNeeded =
    campaign.clickerApprovals.required - campaign.clickerApprovals.granted;

  if (approvalsNeeded == 0) {
    return (
      <Alert severity="success" sx={{ width: "458px", mb: 2 }}>
        All contacts have been approved. You may now start sending.
      </Alert>
    );
  }

  return (
    <Card sx={{ marginBottom: 1, background: "none", mt: 1 }}>
      <CardHeader
        title={
          <Typography variant="body2Bold">Approvals Information</Typography>
        }
        sx={{ height: "28px" }}
      />
      <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
        <TwoColumnDisplay
          labelVariant="body2Bold"
          allRowSx={{ fontSize: "12px" }}
          labelWidth={143}
          showTopBorder={false}
          showBottomBorder={false}
          rows={[
            {
              key: "approvals-needed",
              label: "Approvals Needed",
              content: (
                <Typography variant="body2">
                  {plural(
                    approvalsNeeded ?? 0,
                    "contact needs",
                    "contacts need"
                  )}{" "}
                  to be approved
                </Typography>
              ),
            },
            {
              key: "approval-link",
              label: <FlexBox alignItems="center">Approval Link</FlexBox>,
              content: (
                <ClickToCopy
                  text={approverLink.fullLink}
                  IconButtonProps={{
                    sx: { color: palette.blue, padding: 0, width: "16px" },
                  }}
                  iconSx={{ width: "16px" }}
                  TextProps={{
                    sx: { marginTop: "4px", marginLeft: "8px" },
                  }}
                >
                  <InternalLink
                    to={approverLink.path}
                    target="_blank"
                    sx={{
                      color: palette.blue,
                      textDecoration: "none",
                    }}
                  >
                    {approverLink.fullLink.substring(0, 40)}...
                  </InternalLink>
                </ClickToCopy>
              ),
            },
          ]}
        />
      </CardContent>
    </Card>
  );
};

export const SendDetailsMini = ({
  campaign,
}: {
  campaign: CampaignForView | CampaignForEditor;
}): ReactElement => {
  const { subaccountLink } = useSubaccount()!;
  const subaccount = useSubaccountAdminConfig()!;

  const textingHours = {
    start: campaign.textingHoursStart ?? subaccount.textingHoursStart,
    end: campaign.textingHoursEnd ?? subaccount.textingHoursEnd,
    timezone: campaign.textingHoursTimezone ?? subaccount.textingHoursTimezone,
  };

  const contactImportFile = determineFilename(campaign.contactImportJob);

  const rows: { key: string; label: string; content: ReactNode }[] = [
    {
      key: "contact-list",
      label: "List",
      content: contactImportFile
        ? `Contacts loaded from ${contactImportFile}`
        : "--",
    },
    {
      key: "phone-number",
      label: "Phone Number",
      content: displayPhoneNumber(campaign.messagingStrategy, subaccountLink),
    },
    {
      key: "texting-hours",
      label: "Texting Hours",
      content: `${formatShortTime(textingHours.start)} - ${formatShortTime(
        textingHours.end
      )} ${timezoneDisplayName(textingHours.timezone)}`,
    },
  ];

  return (
    <TwoColumnDisplay
      labelVariant="body2Bold"
      allRowSx={{ fontSize: "12px" }}
      labelWidth={160}
      rows={rows}
    />
  );
};

export const SendDetails = ({
  campaign,
  revealSendStatus,
}: {
  campaign: CampaignForView;
  revealSendStatus: () => void;
}): ReactElement => {
  const { subaccountLink } = useSubaccount()!;
  const subaccount = useSubaccountAdminConfig()!;

  const startedOn = DateTime.fromMillis(campaign.startedAt).toLocaleString(
    DateTime.DATETIME_FULL
  );

  const textingHours = {
    start: campaign.textingHoursStart ?? subaccount.textingHoursStart,
    end: campaign.textingHoursEnd ?? subaccount.textingHoursEnd,
    timezone: campaign.textingHoursTimezone ?? subaccount.textingHoursTimezone,
  };

  const contactImportFile = determineFilename(campaign.contactImportJob);

  // Don't include suppressions in the skipped tallies
  const skippedContacts = _.sumBy(campaign.initialSendFailures ?? [], f =>
    f.initialSendFailureType === InitialMessageSendFailureType.ContactSuppressed
      ? 0
      : f.count
  );

  const sentContacts = campaign.quickStats?.initialSentCount ?? 0;
  const sentOrSkippedContacts = sentContacts + skippedContacts;

  const rows: { key: string; label: string; content: ReactNode }[] = [
    {
      key: "started-on",
      label: "Started On",
      content: startedOn,
    },
    {
      key: "blast-status",
      label: "Send Status",
      content: blastStatusToDisplayText(
        campaign,
        campaign.quickStats?.initialSentCount ?? 0
      ),
    },
    {
      key: "contact-list",
      label: "List",
      content: (
        <>
          {contactImportFile
            ? `${(campaign.quickStats?.contactCount ?? 0).toLocaleString()} contacts loaded from ${contactImportFile}`
            : "--"}
          {campaign.contactImportJob?.__typename ===
            "CampaignContactFollowUpImportJob" && (
            <>
              {" "}
              <FollowUpCampaignFilterSummary
                jobArgs={campaign.contactImportJob.args}
              />
            </>
          )}
        </>
      ),
    },
    {
      key: "campaign-progress",
      label: "Campaign Progress",
      content: (
        <FlexBox justifyContent="space-between">
          <Box>
            {(
              (sentContacts / (campaign.quickStats?.contactCount ?? 1)) *
              100
            ).toFixed(1)}
            % - {sentContacts.toLocaleString()} of{" "}
            {plural(
              campaign.quickStats?.contactCount ?? 0,
              "initial",
              "initials"
            )}{" "}
            sent
          </Box>
          {sentOrSkippedContacts === 0 && (
            <Typography
              variant="body3"
              sx={{
                display: "flex",
                alignItems: "flex-start",
                lineHeight: "19px",
                marginLeft: 2,
              }}
            >
              Some contacts may be skipped
              <InfoToolip sx={{ marginTop: "-3px", marginLeft: "4px" }}>
                Some contacts may be skipped if they were opted out or
                suppressed after your campaign was launched, or if they are on
                an active temporary opt-out list.
              </InfoToolip>
            </Typography>
          )}
          {skippedContacts > 0 && (
            <Box>
              <LinkButton onClick={revealSendStatus}>
                {skippedContacts.toLocaleString()} skipped
              </LinkButton>
            </Box>
          )}
        </FlexBox>
      ),
    },
    {
      key: "phone-number",
      label: "Phone Number",
      content: displayPhoneNumber(campaign.messagingStrategy, subaccountLink),
    },
    {
      key: "texting-hours",
      label: "Texting Hours",
      content: `${formatShortTime(textingHours.start)} - ${formatShortTime(
        textingHours.end
      )} ${timezoneDisplayName(textingHours.timezone)}`,
    },
  ];

  return (
    <TwoColumnDisplay
      labelVariant="body2Bold"
      allRowSx={{ fontSize: "12px", alignItems: "flex-start" }}
      labelWidth={160}
      rows={rows}
    />
  );
};

export const CampaignDetails = ({
  campaign,
  stats,
  hasActBlueIntegration,
  revealSendStatus,
}: {
  campaign: CampaignForView;
  stats: CampaignStatsForDetailQueryResult;
  hasActBlueIntegration: boolean;
  revealSendStatus: () => void;
}): ReactElement => {
  const InitialSendComponent = InitialSendComponents[campaign.status];

  return (
    <FlexBox direction="column" gap={2} sx={{ maxWidth: "600px", mb: 2 }}>
      <Typography sx={{ fontSize: "30px" }}>
        <strong data-testid="campaign-name">{campaign.name}</strong>{" "}
        {statusToDisplayText(
          campaign.status,
          campaign.texterSendEnabled,
          campaign.clickerApprovals.required > campaign.clickerApprovals.granted
        )}
        !
      </Typography>

      <SendDetails campaign={campaign} revealSendStatus={revealSendStatus} />

      <MetadataValuesDetails campaign={campaign} />

      {InitialSendComponent && (
        <InitialSendComponent
          campaign={campaign}
          hasActBlueIntegration={hasActBlueIntegration}
          revealSendStatus={revealSendStatus}
        />
      )}

      {campaign.clickerApprovals.required > 0 && (
        <ApprovalsInformation campaign={campaign} />
      )}

      <TexterInformation campaign={campaign} stats={stats} />
    </FlexBox>
  );
};
